// src/components/InputSection.js

import React, { useContext, useEffect, useCallback } from 'react';
import { FormContext } from '../../context/FormContext';
import { Box, useMediaQuery, useTheme, Typography, Button, IconButton, Chip } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import { v4 as uuidv4 } from 'uuid';
import PersonalInfoCard from './cards/PersonalInfoCard';
import ProfessionalSummaryCard from './cards/ProfessionalSummaryCard';
import WorkExperienceCard from './cards/WorkExperienceCard';
import EducationCard from './cards/EducationCard';
import SkillsCard from './cards/SkillsCard';
import ProjectsCard from './cards/ProjectsCard';
import CertificationsCard from './cards/CertificationsCard';
import AwardsAndHonorsCard from './cards/AwardsAndHonorsCard';
import LanguagesCard from './cards/LanguagesCard';
import VolunteerExperienceCard from './cards/VolunteerExperienceCard';
import PublicationsCard from './cards/PublicationsCard';
import ReferencesCard from './cards/ReferencesCard';
import layout from '../../theme/layout';
import tokens from '../../theme/tokens';
import { useLanguage } from '../../localization';

export const allSections = [
  { name: 'Professional Summary', component: ProfessionalSummaryCard, isArray: false, sectionKey: 'professionalSummary' },
  { name: 'Work Experience', component: WorkExperienceCard, isArray: true, sectionKey: 'workExperiences' },
  { name: 'Education', component: EducationCard, isArray: true, sectionKey: 'educations' },
  { name: 'Skills', component: SkillsCard, isArray: true, sectionKey: 'skills' },
  { name: 'Projects', component: ProjectsCard, isArray: true, sectionKey: 'projects' },
  { name: 'Certifications', component: CertificationsCard, isArray: true, sectionKey: 'certifications' },
  { name: 'Awards and Honors', component: AwardsAndHonorsCard, isArray: true, sectionKey: 'awardsAndHonors' },
  { name: 'Languages', component: LanguagesCard, isArray: true, sectionKey: 'languages' },
  { name: 'Volunteer Experience', component: VolunteerExperienceCard, isArray: true, sectionKey: 'volunteerExperiences' },
  { name: 'Publications', component: PublicationsCard, isArray: true, sectionKey: 'publications' },
  { name: 'References', component: ReferencesCard, isArray: true, sectionKey: 'references' },
];

// Helper function to translate section names
export const getTranslatedSectionName = (sectionName, t) => {
  return t(`inputSection.sections.${sectionName}`) || sectionName;
};

function InputSection({ selectedSections, setSelectedSections, onBackToWelcome }) {
  const { formData, updateFormData } = useContext(FormContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useLanguage();
  
  const unselectedSections = allSections.filter(
    (section) => !selectedSections.some((selected) => selected.name === section.name)
  );

  const handleSelectSection = useCallback((sectionName) => {
    const sectionToSelect = allSections.find((section) => section.name === sectionName);
    if (sectionToSelect) {
      const sectionKey = sectionToSelect.sectionKey;

      const usesFactory = sectionToSelect.component.name === 'GeneratedSectionCard';

      if (sectionKey && sectionToSelect.isArray && usesFactory) {
         const currentArray = formData[sectionKey] || [];
         if (currentArray.length === 0) {
           if (!formData.hasOwnProperty(sectionKey)) {
             updateFormData(sectionKey, []);
           }
         }
      } else if (sectionKey && !sectionToSelect.isArray && !formData.hasOwnProperty(sectionKey)) {
        if (sectionKey === 'professionalSummary') {
            updateFormData(sectionKey, '');
        }
      }

      setSelectedSections(prevSections => {
        if (prevSections.some(s => s.name === sectionName)) {
            return prevSections;
        }
        return [
            ...prevSections,
            {
                ...sectionToSelect,
                expanded: true,
            }
        ];
      });
    }
  }, [updateFormData, formData, setSelectedSections]);

  const handleRemoveSection = useCallback((sectionName) => {
    const sectionToRemoveDetails = allSections.find((s) => s.name === sectionName);
    if (!sectionToRemoveDetails) return;

    const sectionKey = sectionToRemoveDetails.sectionKey;

    setSelectedSections(prevSelectedSections =>
      prevSelectedSections.filter(section => section.name !== sectionName)
    );

    const defaultValue = sectionToRemoveDetails.isArray ? [] : '';
    updateFormData(sectionKey, defaultValue);
  }, [setSelectedSections, updateFormData]);

  const onDragEnd = useCallback((result) => {
    if (!result.destination) return;

    const items = Array.from(selectedSections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSelectedSections(items);
  }, [selectedSections, setSelectedSections]);

  const onRemoveSection = (sectionKeyToRemove) => {
    const updatedSections = selectedSections.filter(
      (section) => section.id !== sectionKeyToRemove
    );
    setSelectedSections(updatedSections);
    // Also remove from local storage if it was persisted there
  };

  return (
    <Box sx={{
      ...layout.mainContent,
      backgroundColor: 'transparent',
      padding: isMobile ? tokens.spacing[2] : layout.mainContent.padding,
    }}>
      {/* Back to Welcome Button */}
      {onBackToWelcome && (
        <Box sx={{ 
          mb: 3, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={onBackToWelcome}
            sx={{
              color: tokens.colors.text.secondary,
              textTransform: 'none',
              fontWeight: tokens.typography.fontWeights.medium,
              fontSize: '0.875rem',
              px: 1,
              py: 0.5,
              borderRadius: tokens.radius.base,
              transition: 'all 0.2s ease',
              '&:hover': {
                color: tokens.colors.primary.main,
                backgroundColor: `${tokens.colors.primary.main}08`,
                transform: 'translateX(-2px)'
              }
            }}
          >
              {t('input.backToStart')}
          </Button>
          
          <Chip
            icon={<HomeIcon sx={{ fontSize: 16 }} />}
            label={t('input.buildingResume')}
            size="small"
            sx={{
              backgroundColor: `${tokens.colors.primary.main}15`,
              color: tokens.colors.primary.main,
              fontWeight: tokens.typography.fontWeights.medium,
              fontSize: '0.75rem',
              '& .MuiChip-icon': {
                color: tokens.colors.primary.main
              }
            }}
          />
        </Box>
      )}

      <PersonalInfoCard />

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="selectedSectionsDroppable">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {selectedSections.map((section, index) => {
                const SectionComponent = section.component;
                
                if (!SectionComponent) return null;

                return (
                  <Draggable 
                    key={section.name} 
                    draggableId={section.name} 
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <Box
                          sx={{
                            '&:not(:last-child)': {
                              borderBottom: `1px solid ${tokens.colors.neutral[200]}`,
                            }
                          }}
                        >
                          <SectionComponent
                            onRemove={() => handleRemoveSection(section.name)}
                            dragHandle={provided.dragHandleProps}
                            isSelected={true}
                          />
                        </Box>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {unselectedSections.length > 0 && (
        <Box sx={{ mt: 3, p: 2, borderTop: `1px solid ${tokens.colors.neutral[200]}` }}>
          <Typography variant="h6" gutterBottom>
            {t('input.addSections')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {unselectedSections.map((section) => (
              <Button
                key={section.name}
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleSelectSection(section.name)}
                sx={{ mb: 1 }}
              >
                {getTranslatedSectionName(section.name, t)}
              </Button>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default InputSection;
